import invariant from 'invariant';

import { algoliaApiGetGradSchoolsSearchIndex } from 'algolia-api/search/algoliaApiGetGradSchoolsSearchIndex';
import { FiltersWithType } from 'algolia-api/search/getSearchUrl';
import { getVolopsSearchIndex } from 'algolia-api/search/getVolopsSearchIndex';
import { sideFacetsForType } from 'algolia-api/search/searchFacets';
import { ListingType } from 'algolia-api/types/ListingType';
import { QueryOptions } from 'algolia-api/types/QueryOptions';
import { SearchFacet } from 'algolia-api/types/SearchFacet';
import { SearchFilters } from 'algolia-api/types/SearchFilters';
import { SearchFiltersWithType } from 'algolia-api/types/SearchFiltersWithType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { StaffTypeFilter } from 'algolia-api/types/StaffTypeFilter';
import { configValue } from 'utils/config';
import { SEARCH_PAGE_SIZE } from 'utils/constants';
import {
  isDefined,
  isEmpty,
  isNotEmpty,
  removeKeys,
  setEquals,
} from 'utils/functional';

import { algoliaApiGetSearchIndex } from './algoliaApiGetSearchIndex';
import getSearchLocationOptions from './getSearchLocationOptions';

const queryStringFilter = (facet: string, s: string) =>
  isNotEmpty(s) ? `${facet}:'${s}'` : '';

const getBooleanValue = (b: string): string => {
  if (b === 'YES') return 'true';
  if (b === 'NO') return 'false';
  return '';
};

export const isGradProgram = (type: string) => type === 'GRAD_PROGRAM';
export const isVolunteering = (type: string) => type === 'VOLOP';

const facetQueryFiltersInternal = (
  filters: SearchFilters | SearchFiltersWithType<string>,
  facets: Array<SearchFacet>,
): Array<string> => {
  const getValue = (
    { id, filterKey, type, name, options }: SearchFacet,
    value: string | string[],
  ): string => {
    if (name === 'recency') {
      let dtValue;
      if (value === 'PAST_24_HOURS') {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        dtValue = Math.floor(yesterday.getTime() / 1000).toString();
      } else if (value === 'PAST_WEEK') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        dtValue = Math.floor(oneWeekAgo.getTime() / 1000).toString();
      } else if (value === 'PAST_MONTH') {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        dtValue = Math.floor(oneMonthAgo.getTime() / 1000).toString();
      } else {
        throw Error(`Invalid value for recency facet: ${value}`);
      }
      return `published > ${dtValue}`;
    }

    if (type === 'date') {
      return isDefined(value)
        ? Object.entries(value)
            .filter((v) => v[1])
            .map(
              (v) =>
                `${v[0].substring(0, v[0].length - 2)}${
                  v[0].endsWith('GT') ? '>' : '<'
                }${v[1]}`,
            )
            .join(' AND ')
        : '';
    }

    if (type === 'string') {
      return queryStringFilter(filterKey || name, value[0]);
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    return typeof value === 'string'
      ? // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-nested-ternary
        type === 'boolean'
        ? queryStringFilter(name, getBooleanValue(value))
        : value === 'ALL'
        ? ''
        : queryStringFilter(name, value)
      : // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      Array.isArray(value) && type === 'array'
      ? isEmpty(value) ||
        (setEquals(value, options) &&
          id !== 'articleTagsFacet' &&
          id !== 'articleAuthorFacet')
        ? ''
        : `(${value.map((o) => queryStringFilter(name, o)).join(' OR ')})`
      : '';
  };

  const type = filters.type || ''; // using '' for null for flow x_x

  const typeFilter =
    type !== 'ALL' && isNotEmpty(type) ? [queryStringFilter('type', type)] : [];
  return (
    facets
      .filter((f) => (filters[f.id] ? filters[f.id] : filters[f.name]))
      // TODO: Fix this the next time the file is edited.
      // @ts-expect-error fix types
      .map((f) => getValue(f, filters[f.id] ? filters[f.id] : filters[f.name]))
      .filter(isNotEmpty)
      .concat(typeFilter)
  );
};

const facetQueryFilters = (
  filters: FiltersWithType,
  searchFacets: Readonly<Record<string, SearchFacet>>,
) => {
  const facets = sideFacetsForType(searchFacets, filters.type);
  invariant(filters.type, 'type required');
  return facetQueryFiltersInternal(filters, facets);
};

const gradSchoolsFacetQueryFilters = (
  filters: FiltersWithType,
  searchFacets: Readonly<Record<string, SearchFacet>>,
): Array<string> => {
  const facets = Object.values(searchFacets);
  const allowedFilters = removeKeys(filters, ['type']);
  return facetQueryFiltersInternal(allowedFilters, facets);
};

// Happens to be the same as the grad school facet query filter: no 'type'
// Can change if the two ever diverge
const volopsFacetQueryFilters = gradSchoolsFacetQueryFilters;
export const postSearchFacetQueryFilters = (
  filters: SearchFilters,
  searchFacets: Readonly<Record<string, SearchFacet>>,
  locale: string,
): Array<string> => {
  const facets = Object.values(searchFacets);
  return facetQueryFiltersInternal(filters, facets).concat([
    'pageType:POST',
    `locale:${locale}`,
  ]);
};
export const getStaffSearchQueryOptions = (
  searchFacets: Readonly<Record<string, SearchFacet>>,
  {
    location,
    filters,
    pageIndex,
    radius,
  }: {
    location: SearchLocation | null | undefined;
    filters: SearchFiltersWithType<StaffTypeFilter>;
    pageIndex: number;
    radius: string;
  },
): QueryOptions => {
  let searchFilters = facetQueryFilters(filters, searchFacets);
  const index = configValue('algolia', 'staffIndexName');
  if (!index) throw Error('search index not retreived');
  const hitsPerPage = SEARCH_PAGE_SIZE;
  const page = pageIndex || 0;
  const attributesToRetrieve = [
    'objectID',
    'type',
    'status',
    'published',
    'created',
    'expires',
    'archived',
    'name',
    'city',
    'state',
    'country',
    'orgID',
    'orgName',
    'orgType',
    'logoHandle',
    'logo',
    'localizedStarts',
    'localizedEnds',
    'invoiceNumber',
    'method',
    'transactionId',
    'orgs',
    'email',
    'hiring',
  ];
  let locationOptions = {};

  if (radius && location?.latitude && location.longitude) {
    const options = getSearchLocationOptions(radius, location);
    searchFilters = searchFilters.concat(options.locationFilters);
    locationOptions = options.locationOptions;
  }

  return {
    index,
    facets: ['*'],
    hitsPerPage,
    page,
    attributesToRetrieve,
    filters: searchFilters.join(' AND '),
    ...locationOptions,
    removeStopWords: true,
    ignorePlurals: true,
    advancedSyntax: true,
    queryLanguages: ['en', 'es', 'pt'],
  };
};

const getSearchQueryOptions = (
  searchFacets: Readonly<Record<string, SearchFacet>>,
  {
    location,
    filters,
    pageIndex,
    sort,
    radius,
    isMapView,
  }: {
    location: SearchLocation | null | undefined;
    filters?: Readonly<SearchFiltersWithType<ListingType>>;
    pageIndex: number;
    sort: string | null | undefined;
    radius: string;
    isMapView?: boolean | null | undefined;
  },
): QueryOptions => {
  const type = !filters ? 'JOBS' : filters.type;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  let searchFilters = !filters
    ? []
    : // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    isGradProgram(type)
    ? gradSchoolsFacetQueryFilters(filters, searchFacets)
    : isVolunteering(type)
    ? volopsFacetQueryFilters(filters, searchFacets)
    : facetQueryFilters(filters, searchFacets);
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  const index = !filters
    ? algoliaApiGetSearchIndex(sort)
    : // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    isGradProgram(type)
    ? algoliaApiGetGradSchoolsSearchIndex(sort)
    : // TODO: Fix this the next time the file is edited.
    isVolunteering(type)
    ? // TODO: Fix this the next time the file is edited.
      // @ts-expect-error fix types
      getVolopsSearchIndex(filters.actionDateFacet, sort)
    : algoliaApiGetSearchIndex(sort);
  if (!index) throw Error('search index not retreived');
  const hitsPerPage = isMapView ? 100 : SEARCH_PAGE_SIZE;
  const attributesToSnippet = ['description:20'];
  const page = isMapView ? 0 : pageIndex || 0;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  const attributesToRetrieve = isGradProgram(type)
    ? [
        'type',
        'objectID',
        'name',
        'schoolName',
        'city',
        'state',
        'country',
        'url',
        'orgID',
        'orgUrl',
        'orgName',
        'orgType',
        'avgFinancialAid',
        'nbOfStudents',
        'isPublicSchool',
        'acceptanceRate',
        'logoHandle',
        'logo',
        'filters',
        'published',
        'degreeProgramsOffered',
      ]
    : isVolunteering(type)
    ? ['*']
    : [
        'objectID',
        'type',
        'published',
        'name',
        'city',
        'state',
        'stateStr',
        'country',
        'url',
        'orgID',
        'orgUrl',
        'orgName',
        'orgType',
        'logoHandle',
        'imageHandle',
        'logo',
        'isFullTime',
        'locationType',
        'remoteZone',
        'paid',
        'payment',
        'localizedStarts',
        'localizedEnds',
        '_geoloc',
        'hasPublishedListings',
        'actionType',
        'source',
        'timezone',
        'hasAts',
        'isPostedAnonymously',
        'hiring',
        'salaryMinimum',
        'salaryMaximum',
        'salaryCurrency',
        'salaryPeriod',
      ];
  let locationOptions = {};

  if (
    filters &&
    filters.hasLocationFacet !== 'NO' && // Special case for this facet to ignore user location
    radius &&
    location?.latitude &&
    location.longitude
  ) {
    const options = getSearchLocationOptions(radius, location);
    searchFilters = searchFilters.concat(options.locationFilters);
    locationOptions = options.locationOptions;
  }

  return {
    index,
    facets: ['*'],
    hitsPerPage,
    page,
    attributesToSnippet,
    attributesToRetrieve,
    filters: searchFilters.join(' AND '),
    ...locationOptions,
    removeStopWords: true,
    ignorePlurals: true,
    advancedSyntax: true,
    queryLanguages: ['en', 'es', 'pt'],
  };
};

// eslint-disable-next-line import/no-default-export
export default getSearchQueryOptions;
